import type { ApiErrorCode, BookingLifecycle } from '@gty/common';

import { correlationIdOf, isApiError } from '../api/errors';

/**
 * When a document stops being editable, and how to say so.
 *
 * THE LOCK IS THE BOOKING'S STATUS, NOT THE DOWNLOAD. Both documents stay
 * editable indefinitely while the trip is ahead of them — including after they
 * have been downloaded, printed and sent. Staff routinely print a draft, notice
 * the pickup point is wrong and fix it; locking on first download would make
 * that impossible and is the obvious wrong design.
 *
 * The server holds this rule (`isLocked(bookingStatus)`), and it is the only
 * thing that actually enforces it. Everything here is so the UI can grey the
 * form out FIRST and explain why, rather than letting someone type for a minute
 * and then handing them a raw 409.
 */

export type DocumentKind = 'voucher' | 'invoice';

export interface DocumentLock {
  locked: boolean;
  /** Present when locked — the sentence shown in place of the editing controls. */
  reason: string | null;
}

const NOUN: Readonly<Record<DocumentKind, string>> = Object.freeze({
  voucher: 'voucher',
  invoice: 'invoice',
});

/**
 * Locked by the booking having reached a terminal state.
 *
 * COMPLETED: the trip has happened, so the document is a record of what
 * happened rather than a plan, and rewriting it would be rewriting history.
 * CANCELLED: there is no trip to document; the server refuses edits outright.
 */
export function lockFor(kind: DocumentKind, bookingStatus: BookingLifecycle): DocumentLock {
  if (bookingStatus === 'COMPLETED') {
    return {
      locked: true,
      reason:
        `This trip is completed, so its ${NOUN[kind]} is locked. A completed trip’s details ` +
        'are a record of what happened and cannot be rewritten — issue a credit note or a new ' +
        'document if something must change.',
    };
  }
  if (bookingStatus === 'CANCELLED') {
    return {
      locked: true,
      reason: `This booking is cancelled, so its ${NOUN[kind]} can no longer be edited.`,
    };
  }
  return { locked: false, reason: null };
}

export interface DocumentSaveError {
  /** True when the server refused because the document is frozen. */
  locked: boolean;
  message: string;
  correlationId: string | null;
  /**
   * The server's `ApiErrorCode`, when it sent one.
   *
   * Carried through so a caller can branch on a refusal that is neither a lock
   * nor a fault — specifically `BOOKING_VALUE_CHANGE_REQUIRED`, which means "the
   * edit is legitimate but belongs to a different action". Rendering that as a
   * plain red error would tell the user they were wrong when they were only in
   * the wrong place.
   */
  code: ApiErrorCode | null;
}

/**
 * A failed save, explained.
 *
 * A 409 DOCUMENT_LOCKED is surfaced as a LOCKED STATE — the form goes read-only
 * and says why — rather than as a raw error toast the user can dismiss and then
 * keep typing into a form that will never save. It normally means the booking
 * was completed in another tab while this one was open, so the right response is
 * for the UI to catch up with the server, not to complain.
 */
export function describeSaveError(error: unknown, kind: DocumentKind): DocumentSaveError {
  if (!isApiError(error)) {
    return {
      locked: false,
      message: `Could not save the ${NOUN[kind]}. Check your connection and try again.`,
      correlationId: null,
      code: null,
    };
  }

  const correlationId = correlationIdOf(error);

  if (error.code === 'DOCUMENT_LOCKED') {
    return { locked: true, message: error.message, correlationId, code: error.code };
  }

  if (error.status === 403) {
    return {
      locked: false,
      message: `You do not have permission to edit this ${NOUN[kind]}.`,
      correlationId,
      code: error.code,
    };
  }

  if (error.status >= 500) {
    return {
      locked: false,
      message:
        correlationId === null
          ? `${error.message} Your changes were not saved.`
          : `${error.message} Your changes were not saved. If you report this, quote reference ${correlationId}.`,
      correlationId,
      code: error.code,
    };
  }

  return { locked: false, message: error.message, correlationId, code: error.code };
}
